<template>
  <main class="settings-page">
    <header class="settings-header">
      <div>
        <span class="settings-eyebrow">Seu app financeiro</span>
        <h1>Configurações</h1>
        <p>Ajuste seus dados pessoais e gerencie seu plano de casal.</p>
      </div>

      <button
        class="refresh-button"
        type="button"
        :disabled="refreshing"
        aria-label="Atualizar configurações"
        title="Atualizar configurações"
        @click="loadPageData"
      >
        <i class="ph ph-arrow-clockwise" :class="{ 'is-spinning': refreshing }" aria-hidden="true"></i>
      </button>
    </header>

    <section class="settings-hero" aria-labelledby="settings-overview-title">
      <div>
        <span class="settings-hero__label">Resumo do perfil</span>
        <h2 id="settings-overview-title">{{ companyName }}</h2>
        <p>{{ companyEmail }}</p>
      </div>

      <div class="settings-hero__stats" aria-label="Resumo das configurações">
        <span>
          <i class="ph ph-users" aria-hidden="true"></i>
          {{ usersCountLabel }}
        </span>
      </div>
    </section>

    <div class="settings-grid">
      <section class="settings-panel account-panel" aria-labelledby="account-title">
        <div class="settings-panel__heading">
          <div>
            <span class="settings-eyebrow">Perfil</span>
            <h2 id="account-title">Dados pessoais</h2>
            <p>Informações usadas para identificar você dentro do app.</p>
          </div>
          <span class="settings-panel__icon"><i class="ph ph-user-circle" aria-hidden="true"></i></span>
        </div>

        <form class="settings-form" @submit.prevent="requestSaveConfirmation">
          <label class="settings-field">
            <span>Nome completo</span>
            <input v-model="configurationForm.name" type="text" class="form-control" autocomplete="name" required />
          </label>

          <label class="settings-field">
            <span>E-mail</span>
            <input
              v-model="configurationForm.email"
              type="email"
              class="form-control"
              autocomplete="email"
              aria-disabled="true"
              disabled
            />
          </label>

          <label class="settings-field">
            <span>Telefone</span>
            <input
              v-model="configurationForm.phone"
              type="tel"
              inputmode="tel"
              autocomplete="tel"
              maxlength="19"
              class="form-control"
              placeholder="(11) 99999-9999"
              required
              @input="maskPhone"
            />
          </label>

          <div class="settings-field settings-field--wide">
            <span id="company-token-label">Código de amizade</span>
            <div class="token-box">
              <code aria-labelledby="company-token-label">{{ companyToken || 'Token ainda não disponível' }}</code>
              <button type="button" :disabled="!companyToken" aria-label="Copiar código de amizade" @click="copyCompanyToken">
                <i class="ph ph-copy" aria-hidden="true"></i>
                {{ copyTokenFeedback || 'Copiar' }}
              </button>
            </div>
          </div>

          <div v-if="configurationFeedback.message" class="settings-feedback" :class="configurationFeedback.error ? 'settings-feedback--error' : 'settings-feedback--success'">
            <i :class="configurationFeedback.error ? 'ph ph-warning-circle' : 'ph ph-check-circle'" aria-hidden="true"></i>
            <span>{{ configurationFeedback.message }}</span>
          </div>

          <button type="submit" class="save-settings-button" :disabled="savingConfigurations">
            <span>{{ savingConfigurations ? 'Salvando...' : 'Salvar alterações' }}</span>
            <i :class="savingConfigurations ? 'ph ph-spinner-gap is-spinning' : 'ph ph-arrow-right'" aria-hidden="true"></i>
          </button>
        </form>
      </section>

    </div>

    <CoupleSettings />
    <UsersList />

    <div
      v-if="showSaveConfirmation"
      ref="saveConfirmationModal"
      class="profile-confirm-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-confirmation-title"
      tabindex="-1"
      @keydown.esc="closeSaveConfirmation"
    >
      <button
        class="profile-confirm-modal__backdrop"
        type="button"
        aria-label="Fechar confirmação"
        @click="closeSaveConfirmation"
      ></button>

      <form class="profile-confirm-modal__content" @submit.prevent="saveConfigurations">
        <span class="profile-confirm-modal__handle" aria-hidden="true"></span>

        <header class="profile-confirm-modal__header">
          <div>
            <span class="settings-eyebrow">Confirmação de segurança</span>
            <h2 id="save-confirmation-title">Confirme sua senha</h2>
          </div>
          <button type="button" aria-label="Fechar" :disabled="savingConfigurations" @click="closeSaveConfirmation">
            <i class="ph ph-x" aria-hidden="true"></i>
          </button>
        </header>

        <p>Digite sua senha atual para confirmar as alterações no nome e telefone.</p>

        <label class="settings-field">
          <span>Senha atual</span>
          <input
            ref="confirmationPasswordInput"
            v-model="confirmationPassword"
            type="password"
            class="form-control"
            autocomplete="current-password"
            placeholder="Digite sua senha"
            required
          />
        </label>

        <div v-if="confirmationError" class="settings-feedback settings-feedback--error">
          <i class="ph ph-warning-circle" aria-hidden="true"></i>
          <span>{{ confirmationError }}</span>
        </div>

        <div class="profile-confirm-modal__actions">
          <button type="button" class="profile-confirm-modal__cancel" :disabled="savingConfigurations" @click="closeSaveConfirmation">
            Cancelar
          </button>
          <button type="submit" class="profile-confirm-modal__confirm" :disabled="savingConfigurations">
            {{ savingConfigurations ? 'Confirmando...' : 'Confirmar e salvar' }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapActions, mapGetters } from "vuex";
import api from "../../config/axios.js";
import CoupleSettings from "../../components/Financial/CoupleSettings.vue";
import UsersList from "../../components/Users/UsersList.vue";

type ApiError = {
  response?: {
    data?: {
      message?: string;
    };
  };
};

export default defineComponent({
  name: "ConfigurationPage",
  components: {
    UsersList,
    CoupleSettings,
  },
  data() {
    return {
      refreshing: false,
      savingConfigurations: false,
      showSaveConfirmation: false,
      confirmationPassword: "",
      confirmationError: "",
      copyTokenFeedback: "",
      configurationFeedback: {
        message: "",
        error: false,
      },
      configurationForm: {
        name: "",
        email: "",
        phone: "",
      },
    };
  },
  computed: {
    ...mapGetters([
      "getConfigurations",
      "getAllUsersList",
    ]),
    companyName(): string {
      return this.getConfigurations?.name || "Sua empresa";
    },
    companyEmail(): string {
      return this.getConfigurations?.email || "Complete os dados da conta para começar.";
    },
    companyToken(): string {
      return this.getConfigurations?.company_token || "";
    },
    usersCountLabel(): string {
      const count = this.getAllUsersList.length;
      return `${count} ${count === 1 ? 'usuário' : 'usuários'}`;
    },
  },
  watch: {
    getConfigurations: {
      immediate: true,
      handler(configurations) {
        if (!configurations) {
          return;
        }

        this.configurationForm.name = configurations.name || "";
        this.configurationForm.email = configurations.email || "";
        this.configurationForm.phone = this.formatPhone(configurations.phone || "");
      },
    },
  },
  methods: {
    ...mapActions([
      "setConfiguration",
      "setAllUsersList",
      "setAccessControl",
    ]),
    async loadPageData() {
      if (this.refreshing) return;
      this.refreshing = true;

      try {
        await Promise.all([
          this.setConfiguration(),
          this.setAllUsersList(),
          this.setAccessControl(),
        ]);
      } finally {
        this.refreshing = false;
      }
    },
    requestSaveConfirmation() {
      this.configurationFeedback = { message: "", error: false };
      this.confirmationPassword = "";
      this.confirmationError = "";
      this.showSaveConfirmation = true;
      this.$nextTick(() => {
        (this.$refs.confirmationPasswordInput as HTMLInputElement | undefined)?.focus();
      });
    },
    closeSaveConfirmation() {
      if (this.savingConfigurations) return;
      this.showSaveConfirmation = false;
      this.confirmationPassword = "";
      this.confirmationError = "";
    },
    async saveConfigurations() {
      if (this.savingConfigurations) return;

      this.savingConfigurations = true;
      this.confirmationError = "";

      try {
        const response = await api.axios.post("/api/update/configuration", {
          name: this.configurationForm.name,
          phone: this.onlyDigits(this.configurationForm.phone),
          password: this.confirmationPassword,
        });
        this.configurationFeedback = {
          message: response.data?.message || "Configurações atualizadas com sucesso.",
          error: false,
        };
        this.showSaveConfirmation = false;
        this.confirmationPassword = "";
        await this.setConfiguration();
      } catch (error: unknown) {
        const apiError = error as ApiError;
        const message = apiError?.response?.data?.message || "Não foi possível atualizar as configurações.";
        this.confirmationError = message;
      } finally {
        this.savingConfigurations = false;
      }
    },
    async copyCompanyToken() {
      if (!this.companyToken) return;

      try {
        await navigator.clipboard.writeText(this.companyToken);
        this.copyTokenFeedback = "Copiado";
      } catch {
        this.copyTokenFeedback = "Copie manualmente";
      }

      window.setTimeout(() => {
        this.copyTokenFeedback = "";
      }, 2200);
    },
    onlyDigits(value: string): string {
      return String(value || "").replace(/\D/g, "");
    },
    formatPhone(value: string): string {
      let digits = this.onlyDigits(value).slice(0, 13);
      let countryPrefix = "";

      if (digits.startsWith("55") && digits.length > 11) {
        countryPrefix = "+55 ";
        digits = digits.slice(2);
      }

      if (digits.length <= 2) {
        return digits ? `${countryPrefix}(${digits}` : "";
      }

      const areaCode = digits.slice(0, 2);
      const number = digits.slice(2, 11);
      const splitAt = number.length > 8 ? 5 : 4;
      const firstPart = number.slice(0, splitAt);
      const secondPart = number.slice(splitAt);

      return `${countryPrefix}(${areaCode}) ${firstPart}${secondPart ? `-${secondPart}` : ""}`;
    },
    maskPhone(event: Event) {
      const input = event.target as HTMLInputElement;
      this.configurationForm.phone = this.formatPhone(input.value);
    },
  },
  mounted() {
    this.loadPageData();
  },
});
</script>

<style scoped>
.settings-page {
  --settings-accent: #06a77d;
  --settings-accent-dark: #05785d;
  --settings-danger: #c74f4a;
  --settings-ink: #171629;
  --settings-muted: #747487;
  --settings-surface: #ffffff;
  --settings-soft: #f4f5f8;
  --settings-border: #e9e9f0;
  color: var(--settings-ink);
  margin: 0 auto;
  max-width: 1480px;
  padding-bottom: 8px;
  width: 100%;
}

.settings-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}

.settings-eyebrow,
.settings-hero__label {
  color: var(--settings-accent-dark);
  display: block;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.settings-header h1 {
  color: var(--settings-ink);
  font-size: clamp(24px, 3vw, 31px);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.12;
  margin: 0;
}

.settings-header p,
.settings-panel__heading p,
.settings-hero p {
  color: var(--settings-muted);
  font-size: 13px;
  line-height: 1.45;
  margin: 5px 0 0;
}

.refresh-button {
  align-items: center;
  background: var(--settings-surface);
  border: 1px solid var(--settings-border);
  border-radius: 50%;
  color: var(--settings-ink);
  display: inline-flex;
  flex: 0 0 44px;
  font-size: 20px;
  height: 44px;
  justify-content: center;
  transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.refresh-button:hover:not(:disabled) {
  border-color: var(--settings-accent);
  color: var(--settings-accent-dark);
  transform: translateY(-1px);
}

.refresh-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.settings-hero {
  align-items: center;
  background: var(--settings-surface);
  border: 1px solid var(--settings-border);
  border-radius: 22px;
  box-shadow: 0 10px 30px rgba(23, 22, 41, 0.035);
  display: grid;
  gap: 22px;
  grid-template-columns: minmax(0, 1fr) auto;
  margin-bottom: 16px;
  overflow: hidden;
  padding: 24px 26px;
  position: relative;
}

.settings-hero::before {
  background: var(--settings-accent);
  border-radius: 0 999px 999px 0;
  bottom: 22px;
  content: "";
  left: 0;
  position: absolute;
  top: 22px;
  width: 5px;
}

.settings-hero h2 {
  color: var(--settings-ink);
  font-size: clamp(25px, 4vw, 36px);
  font-weight: 800;
  letter-spacing: -0.045em;
  line-height: 1.08;
  margin: 0;
  overflow-wrap: anywhere;
}

.settings-hero__stats {
  display: grid;
  gap: 9px;
  grid-template-columns: minmax(0, 1fr);
  min-width: min(100%, 180px);
}

.settings-hero__stats span {
  align-items: center;
  background: #e8f8f3;
  border: 1px solid #c9eee2;
  border-radius: 15px;
  color: var(--settings-accent-dark);
  display: flex;
  font-size: 12px;
  font-weight: 800;
  gap: 8px;
  min-height: 52px;
  padding: 0 13px;
  white-space: nowrap;
}

.settings-hero__stats i {
  font-size: 18px;
}

.settings-grid {
  align-items: start;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
  margin-bottom: 16px;
}

.settings-panel {
  background: var(--settings-surface);
  border: 1px solid var(--settings-border);
  border-radius: 22px;
  min-width: 0;
  padding: 22px;
}

.settings-panel__heading {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-bottom: 20px;
}

.settings-panel__heading h2 {
  color: var(--settings-ink);
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
}

.settings-panel__icon {
  align-items: center;
  background: #e8f8f3;
  border-radius: 16px;
  color: var(--settings-accent-dark);
  display: inline-flex;
  flex: 0 0 54px;
  font-size: 25px;
  height: 54px;
  justify-content: center;
  width: 54px;
}

.settings-form {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.settings-field {
  display: block;
  min-width: 0;
}

.settings-field--wide {
  grid-column: 1 / -1;
}

.settings-field > span {
  color: var(--settings-ink);
  display: block;
  font-size: 11px;
  font-weight: 800;
  margin-bottom: 7px;
}

.settings-form .form-control {
  background-color: var(--settings-surface);
  border: 1px solid var(--settings-border);
  border-radius: 13px;
  color: var(--settings-ink);
  font-size: 13px;
  min-height: 46px;
  padding: 10px 13px;
}

.settings-form .form-control:focus {
  border-color: var(--settings-accent);
  box-shadow: 0 0 0 0.2rem rgba(6, 167, 125, 0.12);
}

.settings-form .form-control:disabled {
  background: var(--settings-soft);
  color: var(--settings-muted);
  cursor: not-allowed;
  opacity: 0.62;
}

.token-box {
  align-items: center;
  background: var(--settings-soft);
  border: 1px solid var(--settings-border);
  border-radius: 15px;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 50px;
  padding: 7px 8px 7px 14px;
}

.token-box code {
  color: var(--settings-ink);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.token-box button {
  align-items: center;
  border: 0;
  border-radius: 12px;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  justify-content: center;
  min-height: 40px;
  padding: 0 13px;
}

.token-box button {
  background: #e8f8f3;
  color: var(--settings-accent-dark);
}

.token-box button:disabled,
.save-settings-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.settings-feedback {
  align-items: center;
  border-radius: 14px;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 8px;
  grid-column: 1 / -1;
  padding: 12px 14px;
}

.settings-feedback--success {
  background: #e8f8f3;
  border: 1px solid #c9eee2;
  color: var(--settings-accent-dark);
}

.settings-feedback--error {
  background: #fff1f0;
  border: 1px solid #ffd5d1;
  color: #9c3732;
}

.save-settings-button {
  align-items: center;
  background: var(--settings-accent);
  border: 0;
  border-radius: 13px;
  color: #fff;
  display: flex;
  font-size: 14px;
  font-weight: 800;
  grid-column: 1 / -1;
  justify-content: space-between;
  min-height: 48px;
  padding: 0 17px;
}

.profile-confirm-modal {
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 20px;
  position: fixed;
  z-index: 2050;
}

.profile-confirm-modal__backdrop {
  background: rgba(15, 23, 42, 0.58);
  border: 0;
  inset: 0;
  position: absolute;
  width: 100%;
}

.profile-confirm-modal__content {
  background: var(--settings-surface);
  border: 1px solid var(--settings-border);
  border-radius: 22px;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.24);
  max-width: 440px;
  padding: 24px;
  position: relative;
  width: 100%;
  z-index: 1;
}

.profile-confirm-modal__handle {
  display: none;
}

.profile-confirm-modal__header {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
}

.profile-confirm-modal__header h2 {
  color: var(--settings-ink);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
}

.profile-confirm-modal__header > button {
  align-items: center;
  background: var(--settings-soft);
  border: 0;
  border-radius: 50%;
  color: var(--settings-ink);
  display: inline-flex;
  flex: 0 0 40px;
  font-size: 19px;
  height: 40px;
  justify-content: center;
}

.profile-confirm-modal__content > p {
  color: var(--settings-muted);
  font-size: 13px;
  line-height: 1.5;
  margin: 12px 0 20px;
}

.profile-confirm-modal__content .form-control {
  background: var(--settings-surface);
  border: 1px solid var(--settings-border);
  border-radius: 13px;
  color: var(--settings-ink);
  min-height: 48px;
  padding: 10px 13px;
}

.profile-confirm-modal__content .form-control:focus {
  border-color: var(--settings-accent);
  box-shadow: 0 0 0 0.2rem rgba(6, 167, 125, 0.12);
}

.profile-confirm-modal__content .settings-feedback {
  margin-top: 14px;
}

.profile-confirm-modal__actions {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1.35fr;
  margin-top: 20px;
}

.profile-confirm-modal__actions button {
  border: 0;
  border-radius: 13px;
  font-size: 13px;
  font-weight: 800;
  min-height: 46px;
  padding: 0 14px;
}

.profile-confirm-modal__cancel {
  background: var(--settings-soft);
  color: var(--settings-ink);
}

.profile-confirm-modal__confirm {
  background: var(--settings-accent);
  color: #fff;
}

.profile-confirm-modal__actions button:disabled,
.profile-confirm-modal__header > button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.is-spinning {
  animation: settings-spin 0.9s linear infinite;
}

:deep(.settings-list-panel) {
  background: var(--settings-surface);
  border: 1px solid var(--settings-border);
  border-radius: 22px;
  margin-bottom: 16px;
  padding: 22px;
}

:deep(.settings-section-heading) {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 20px;
}

:deep(.settings-section-heading h2) {
  color: var(--settings-ink);
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
}

:deep(.settings-section-heading p) {
  color: var(--settings-muted);
  font-size: 13px;
  line-height: 1.45;
  margin: 5px 0 0;
}

:deep(.settings-create-button) {
  align-items: center;
  background: #e8f8f3;
  border: 1px solid #c9eee2;
  border-radius: 999px;
  color: var(--settings-accent-dark);
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  min-height: 44px;
  padding: 0 12px;
  text-decoration: none;
}

:deep(.settings-create-button:hover) {
  background: #d9f3eb;
  border-color: #aee4d4;
  color: var(--settings-accent-dark);
}

:deep(.settings-table-wrap) {
  overflow-x: auto;
}

:deep(.settings-data-table) {
  border-collapse: collapse;
  color: var(--settings-ink);
  min-width: 760px;
  width: 100%;
}

:deep(.settings-data-table th) {
  color: var(--settings-muted);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 0 12px 12px;
  text-transform: uppercase;
  white-space: nowrap;
}

:deep(.settings-data-table td) {
  border-top: 1px solid var(--settings-border);
  color: var(--settings-ink);
  font-size: 13px;
  font-weight: 600;
  padding: 14px 12px;
  vertical-align: middle;
}

:deep(.settings-data-table th:first-child),
:deep(.settings-data-table td:first-child) {
  padding-left: 0;
}

:deep(.settings-data-table th:last-child),
:deep(.settings-data-table td:last-child) {
  padding-right: 0;
}

:deep(.settings-primary-cell) {
  align-items: center;
  display: flex;
  gap: 11px;
  min-width: 0;
}

:deep(.settings-avatar) {
  align-items: center;
  background: #e8f8f3;
  border-radius: 13px;
  color: var(--settings-accent-dark);
  display: inline-grid;
  flex: 0 0 42px;
  font-size: 20px;
  height: 42px;
  justify-content: center;
  line-height: 1;
  place-items: center;
  width: 42px;
}

:deep(.settings-avatar i) {
  display: block;
  line-height: 1;
  transform: translateY(1px);
}

:deep(.settings-primary-cell strong),
:deep(.settings-primary-cell > div > span) {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.settings-primary-cell strong) {
  color: var(--settings-ink);
  font-size: 13px;
  font-weight: 800;
}

:deep(.settings-primary-cell > div > span) {
  color: var(--settings-muted);
  font-size: 11px;
  margin-top: 2px;
}

:deep(.status-pill) {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 10px;
  font-weight: 800;
  min-height: 26px;
  padding: 0 9px;
  white-space: nowrap;
}

:deep(.status-pill.is-active) {
  background: #e8f8f3;
  color: var(--settings-accent-dark);
}

:deep(.status-pill.is-inactive) {
  background: #fff0ee;
  color: var(--settings-danger);
}

:deep(.settings-mobile-list) {
  display: none;
}

:deep(.settings-list-empty) {
  align-items: center;
  color: var(--settings-muted);
  display: flex;
  flex-direction: column;
  padding: 38px 12px 22px;
  text-align: center;
}

:deep(.settings-list-empty span) {
  align-items: center;
  background: #e8f8f3;
  border-radius: 50%;
  color: var(--settings-accent-dark);
  display: flex;
  font-size: 27px;
  height: 58px;
  justify-content: center;
  margin-bottom: 12px;
  width: 58px;
}

:deep(.settings-list-empty strong) {
  color: var(--settings-ink);
  font-size: 16px;
  font-weight: 800;
}

:deep(.settings-list-empty p) {
  margin: 5px 0 0;
}

:deep(.modal-content) {
  background: var(--settings-surface);
  border: 0;
  border-radius: 22px;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.24);
  color: var(--settings-ink);
  overflow: hidden;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--settings-border);
  padding: 20px 24px;
}

:deep(.modal-title) {
  color: var(--settings-ink);
  font-size: 22px !important;
  font-weight: 800;
  letter-spacing: -0.03em;
}

:deep(.modal-body) {
  color: var(--settings-muted);
  padding: 22px 24px !important;
}

:deep(.modal-body p) {
  color: var(--settings-muted);
  font-size: 13px;
  line-height: 1.5;
}

:deep(.modal-footer) {
  border-top: 1px solid var(--settings-border);
  gap: 9px;
  padding: 16px 24px;
}

:deep(.modal .form-label),
:deep(.modal h5),
:deep(.modal .form-check-label) {
  color: var(--settings-ink);
}

:deep(.modal .form-label),
:deep(.modal h5) {
  font-size: 12px;
  font-weight: 800;
}

:deep(.modal .form-control) {
  background-color: var(--settings-surface);
  border: 1px solid var(--settings-border);
  border-radius: 13px;
  color: var(--settings-ink) !important;
  min-height: 46px;
}

:deep(.modal .form-check-input) {
  border-color: var(--settings-border);
}

:deep(.modal .form-check-input:checked) {
  background-color: var(--settings-accent);
  border-color: var(--settings-accent);
}

:deep(.modal .btn) {
  border: 0;
  border-radius: 12px;
  font-weight: 800;
  min-height: 44px;
  padding: 0 16px;
}

:deep(.modal .btn-success) {
  background: var(--settings-accent);
}

:deep(.modal .btn-danger) {
  background: var(--settings-danger);
}

:deep(.modal .btn-secondary),
:deep(.modal .btn-dark) {
  background: var(--settings-soft);
  color: var(--settings-ink);
}

:deep(.modal .alert-success),
:deep(.modal .toast.text-bg-success) {
  background: #e8f8f3 !important;
  border: 1px solid #c9eee2 !important;
  color: var(--settings-accent-dark) !important;
}

:deep(.modal .alert-danger),
:deep(.modal .toast.bkred) {
  background: #fff1f0 !important;
  border: 1px solid #ffd5d1 !important;
  color: #9c3732 !important;
}

@media (max-width: 1199px) {
  .settings-hero {
    align-items: stretch;
    grid-template-columns: 1fr;
  }

  .settings-hero__stats {
    min-width: 0;
  }

  .settings-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .settings-header {
    margin-bottom: 14px;
  }

  .settings-header h1 {
    font-size: 24px;
  }

  .settings-header p {
    font-size: 12px;
    max-width: 290px;
  }

  .settings-hero {
    border-radius: 20px;
    gap: 17px;
    padding: 21px 17px 20px 23px;
  }

  .settings-hero::before {
    bottom: 20px;
    top: 20px;
    width: 4px;
  }

  .settings-hero h2 {
    font-size: 27px;
  }

  .settings-hero__stats {
    gap: 7px;
    grid-template-columns: 1fr;
  }

  .settings-hero__stats span {
    min-height: 42px;
  }

  .settings-panel,
  :deep(.settings-list-panel) {
    border-radius: 20px;
    padding: 18px 16px;
  }

  .settings-panel__heading,
  :deep(.settings-section-heading) {
    margin-bottom: 17px;
  }

  .settings-panel__heading h2,
  :deep(.settings-section-heading h2) {
    font-size: 19px;
  }

  .settings-panel__heading p,
  :deep(.settings-section-heading p) {
    font-size: 12px;
  }

  .settings-panel__icon {
    border-radius: 14px;
    flex-basis: 46px;
    font-size: 22px;
    height: 46px;
    width: 46px;
  }

  .settings-form {
    grid-template-columns: 1fr;
  }

  .token-box {
    grid-template-columns: 1fr;
  }

  .token-box code {
    white-space: normal;
    word-break: break-all;
  }

  .token-box button {
    width: 100%;
  }

  .profile-confirm-modal {
    align-items: flex-end;
    padding: 0;
  }

  .profile-confirm-modal__content {
    border-radius: 24px 24px 0 0;
    padding: 14px 20px max(20px, calc(16px + env(safe-area-inset-bottom)));
  }

  .profile-confirm-modal__handle {
    background: var(--settings-border);
    border-radius: 999px;
    display: block;
    height: 4px;
    margin: 0 auto 18px;
    width: 44px;
  }

  .profile-confirm-modal__actions {
    grid-template-columns: 1fr;
  }

  :deep(.settings-create-button) {
    min-height: 44px;
    padding: 0 10px;
  }

  :deep(.settings-table-wrap) {
    display: none;
  }

  :deep(.settings-mobile-list) {
    display: grid;
    gap: 10px;
  }

  :deep(.settings-mobile-card) {
    background: var(--settings-surface);
    border: 1px solid var(--settings-border);
    border-radius: 17px;
    display: grid;
    gap: 12px;
    padding: 13px;
  }

  :deep(.settings-mobile-card__top) {
    align-items: center;
    display: grid;
    gap: 10px;
    grid-template-columns: 42px minmax(0, 1fr) auto;
  }

  :deep(.settings-mobile-card__body) {
    color: var(--settings-muted);
    display: grid;
    font-size: 11px;
    gap: 6px;
  }

  :deep(.settings-mobile-card__body span) {
    overflow-wrap: anywhere;
  }

  :deep(.modal-dialog) {
    align-items: flex-end;
    display: flex;
    margin: 0;
    min-height: 100%;
    padding: 0;
  }

  :deep(.modal-content) {
    border-radius: 24px 24px 0 0;
    max-height: calc(100vh - 20px);
    overflow-y: auto;
    padding-bottom: env(safe-area-inset-bottom);
    width: 100%;
  }

  :deep(.modal-header) {
    padding: 18px 20px 14px;
  }

  :deep(.modal-body) {
    padding: 18px 20px !important;
  }

  :deep(.modal-footer) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 14px 20px max(18px, calc(14px + env(safe-area-inset-bottom)));
  }
}

body.dark .settings-page {
  --settings-ink: #f5f5f8;
  --settings-muted: #aaa9ba;
  --settings-surface: #29283b;
  --settings-soft: #343347;
  --settings-border: #3b394e;
}

body.dark .settings-page .settings-hero__stats span,
body.dark .settings-page .settings-panel__icon,
body.dark .settings-page :deep(.settings-create-button),
body.dark .settings-page :deep(.settings-avatar),
body.dark .settings-page :deep(.status-pill.is-active),
body.dark .settings-page :deep(.settings-list-empty span),
body.dark .settings-page .settings-feedback--success,
body.dark .settings-page .token-box button {
  background: rgba(6, 180, 138, 0.14);
  border-color: rgba(102, 224, 189, 0.22);
  color: #66e0bd;
}

body.dark .settings-page .settings-feedback--error,
body.dark .settings-page :deep(.status-pill.is-inactive) {
  background: rgba(199, 79, 74, 0.17);
  border-color: rgba(255, 170, 164, 0.2);
  color: #ffaaa4;
}

body.dark .settings-page .settings-form .form-control,
body.dark .settings-page .profile-confirm-modal__content .form-control,
body.dark .settings-page :deep(.modal .form-control) {
  background-color: #343347;
  border-color: #49475d;
  color: #f5f5f8 !important;
}

@keyframes settings-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .is-spinning,
  .refresh-button {
    animation: none;
    transition: none;
  }
}
</style>
